import * as React from 'react';
import { ContextualMenu, Dialog, DialogFooter, DialogType, PrimaryButton, TextField } from '@fluentui/react';
import { addTermin, Termin, updateTermin, useTermin } from 'api/termin';
import { useKunde } from 'api/kunde';
import { Ansprechpartner } from 'api/ansprechpartner';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { DatePicker, DayOfWeek } from '@fluentui/react/lib/DatePicker';
import { datePickerStrings, onFormatDate } from 'utils/datePickerStrings';
import moment from 'moment-timezone';
import { ValueType } from 'react-select';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useBetreuer, useBetreuerByKz } from 'api/betreuer';
import { cloneDeep, isEqual } from 'lodash';
import { SelectOption } from 'formComponents/Select/types';
import { Select } from 'formComponents/Select/Select';
import { Label } from '@fluentui/react/lib/Label';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { getAnsprechpartnerLabel, SpracheLabel, TerminArtLabel } from 'utils/labels';
import { SavingState } from 'api/savingState';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { formStyles } from 'styles/formStyles';
import { Timestamps } from 'formComponents/Timestamps';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { OpenLinkIcon } from 'formComponents/OpenLinkIcon/OpenLinkIcon';
import { EinheitByCode } from 'formComponents/lookups/EinheitByCode';
import { FormDecimalField, FormDecimalFieldOptions } from 'formComponents/FormDecimalField';
import { createKundeUrl } from 'utils/formatter';
import { logDifference } from 'utils/helper';
import { ArtikelDetailSelect } from '../Lager/ArtikelDetail/ArtikelDetailSelect';
import { TerminAttachmentList } from './TerminAttachmentList';
import { AddTerminAttachmentsData } from './types';
import { AddTerminAttachments } from './AddTerminAttachments';

const styles = mergeStyleSets({
    contactLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '3fr 3fr 2fr',
        gridColumnGap: 10,
    },
    dateLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '1fr 60px 1fr 60px',
        gridColumnGap: 10,
    },
    detailsAndAttachments: {
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        gridColumnGap: 10,
    },
    title: {
        position: 'relative',
        width: 'fit-content',
    },
    titleLinkIcon: {
        top: '2px !important',
        right: '-20px !important',
        fontSize: '14px !important',
    },
    artikelLine: {
        display: 'grid',
        gridTemplateColumns: '1fr 150px 150px',
        gridColumnGap: 10,
    },
});

export interface TerminEditData {
    kNr?: number;
    zfKaNr: number;
    initUserKz?: string;
    initKaNr?: number;
    initKaName?: string;
    initTerminArt?: string;
    initTerminBetreff?: string;
    initArtikelGruppeNr?: number;
    initArtikelNr?: string;
    initPosText?: string;
    initPosEinh?: string;
    initPosMenge?: number;
}

interface TerminEditProps {
    defaultKNr?: number;
    zfKaNr: number; // -1 = new
    initUserKz?: string;
    initKaNr?: number;
    initKaName?: string;
    initTerminArt?: string;
    initTerminBetreff?: string;
    initArtikelGruppeNr?: number;
    initArtikelNr?: string;
    initPosText?: string;
    initPosEinh?: string;
    initPosMenge?: number;
    onClose: (refetch: boolean) => void;
}

export const TerminEdit: React.FC<TerminEditProps> = ({
    defaultKNr,
    zfKaNr,
    initUserKz,
    initKaNr,
    initKaName,
    initTerminArt,
    initTerminBetreff,
    initArtikelGruppeNr,
    initArtikelNr,
    initPosText,
    initPosEinh,
    initPosMenge,
    onClose,
}) => {
    const auth = useSelector(authSelector);
    const betreuer = useBetreuer();
    const betreuerByKz = useBetreuerByKz();

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);

    const [zeitVonS, setZeitVonS] = React.useState('');
    const [zeitBisS, setZeitBisS] = React.useState('');

    const [termin, setTermin] = React.useState<Termin | null>(null);
    const dbTermin = useTermin(
        {
            zfKaNr,
            withKunde: true,
            withTerminAttachments: true,
            includeArchiv: true,
        },
        {
            enabled: zfKaNr !== -1,
            onSuccess: (loadedTermin) => {
                setTermin(cloneDeep(loadedTermin));
                setZeitVonS(loadedTermin.zeitVon.toLocaleString('de-AT'));
                setZeitBisS(loadedTermin.zeitBis.toLocaleString('de-AT'));
                setSavingState(SavingState.notChanged);
            },
        },
    );

    const [addTerminAttachmentData, setAddTerminAttachmentsData] = React.useState<AddTerminAttachmentsData | null>(
        null,
    );

    const renderEinheit = (column: keyof Termin, label: string) => (
        <EinheitByCode
            label={label}
            disabled={savingState === SavingState.saving}
            value={(termin?.[column] as string) || ''}
            menuPlacement="top"
            allowEmpty
            onChange={(newEinheit) => {
                if (termin) {
                    setTermin({
                        ...termin,
                        posEinh: newEinheit,
                    });
                }
            }}
        />
    );

    const renderDecimalField = (column: keyof Termin, label: string, options: FormDecimalFieldOptions = {}) => (
        <FormDecimalField
            value={(termin?.[column] || '') as number}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled }}
            onChange={(newValue): void => {
                if (termin) {
                    setTermin({
                        ...termin,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const kunde = useKunde(
        {
            nr: termin?.kNr,
            withAnsprechpartnerList: true,
        },
        {
            enabled: termin?.kNr !== undefined,
        },
    );

    React.useEffect(() => {
        if (zfKaNr === -1) {
            const now = moment();
            const datum = moment(now).format('YYYY-MM-DD') + 'T00:00:00';
            const zeit = now.hour() + now.minute() / 100;
            setTermin({
                kNr: defaultKNr || 0,
                kaNr: initKaNr || 0,
                kaName: initKaName || '',
                datumVon: datum,
                zeitVon: zeit,
                datumBis: datum,
                zeitBis: zeit,
                terminArt: initTerminArt || 'Z',
                userKz: initUserKz || auth.kz || '?',
                terminOrt: '',
                terminBetreff: initTerminBetreff || '',
                terminText: '',
                terminStatus: false,
                prodInfo: false,
                mailKd: false,
                mailKdBem: false,
                mailPdf: false,
                artikelGruppeNr: initArtikelGruppeNr || 0,
                artikelNr: initArtikelNr || '',
                posText: initPosText || '',
                posEinh: initPosEinh || '',
                posMenge: initPosMenge || 0,
                syncOutlook: false,
            });
            setZeitVonS(zeit.toLocaleString('de-AT'));
            setZeitBisS(zeit.toLocaleString('de-AT'));
            setSavingState(SavingState.notSaved);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (
            [SavingState.notSaved, SavingState.notChanged].includes(savingState) &&
            !dbTermin.isLoading &&
            !dbTermin.isFetching &&
            termin !== null
        ) {
            if (!isEqual(dbTermin.data, termin)) {
                setSavingState(SavingState.notSaved);
                logDifference('termin', termin, dbTermin.data);
            } else if (savingState !== SavingState.notChanged) {
                setSavingState(SavingState.notChanged);
            }
        }
    }, [savingState, dbTermin, termin]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const save = () => {
        if (termin && savingState !== SavingState.saving) {
            setSavingState(SavingState.saving);
            if (zfKaNr === -1) {
                addTermin(termin)
                    .then(() => {
                        setSavingState(SavingState.saved);
                        /*
                        if (isMounted) {
                            setSavingState(SavingState.notChanged);
                        }
                         */
                        onClose(true);
                    })
                    .catch((error) => {
                        console.error(error);
                        setSavingState(SavingState.error);
                    });
            } else {
                updateTermin(termin)
                    .then(() => {
                        setSavingState(SavingState.saved);

                        /*
                        if (isMounted) {
                            setSavingState(SavingState.notChanged);
                        }
                         */

                        onClose(true);
                    })
                    .catch((error) => {
                        console.error(error);
                        setSavingState(SavingState.error);
                    });
            }
        }
    };

    const ansprechpartner: Ansprechpartner | null =
        (kunde.data?.ansprechpartnerList || []).find((a) => a.nr === termin?.kaNr) || null;

    const renderArtikelDetailSelect = () => (
        <ArtikelDetailSelect
            artikelGruppeNr={termin?.artikelGruppeNr}
            artikelNr={termin?.artikelNr}
            disabled={savingState === SavingState.saving}
            onChange={async (newArtikelDetail) => {
                if (termin) {
                    setTermin({
                        ...termin,
                        artikelGruppeNr: newArtikelDetail?.artikelGruppeNr || 0,
                        artikelNr: newArtikelDetail?.artikelNr || '',
                    });
                }
            }}
        />
    );

    return (
        <>
            {routerConfirmMessage.render()}
            <Dialog
                hidden={false}
                title={
                    <div className={styles.title}>
                        {`Tätigkeit bei ${kunde.data?.name1 || '...'} ${kunde.data?.name2 || ''}`}
                        {kunde ? (
                            <OpenLinkIcon
                                className={styles.titleLinkIcon}
                                link={createKundeUrl(kunde.data?.nr.toString())}
                            />
                        ) : null}
                    </div>
                }
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth="90%"
                maxWidth="90%"
                onDismiss={() => {
                    onClose(false);
                }}
            >
                {dbTermin.isLoading ||
                dbTermin.isFetching ||
                kunde.isLoading ||
                kunde.isFetching ||
                !termin ||
                !kunde ? (
                    <LoadingIndicator />
                ) : (
                    <form>
                        <div className={formStyles.topBlock}>
                            <div>
                                {kunde.data?.plz || ''} {kunde.data?.ort || ''}, {kunde.data?.strasse || ''}{' '}
                                {kunde.data?.land || ('' && `(${kunde.data?.land || ''})`)}
                            </div>
                            <Checkbox
                                label="An Outlook übertragen"
                                disabled={savingState === SavingState.saving}
                                className={formStyles.checkbox}
                                checked={termin.syncOutlook || false}
                                onChange={(event, checked) => {
                                    setTermin({
                                        ...termin,
                                        syncOutlook: checked || false,
                                    });
                                }}
                            />
                            <Checkbox
                                label="Erledigt"
                                disabled={savingState === SavingState.saving}
                                className={formStyles.checkbox}
                                checked={termin.terminStatus || false}
                                onChange={(event, checked) => {
                                    setTermin({
                                        ...termin,
                                        terminStatus: checked || false,
                                    });
                                }}
                            />
                        </div>
                        <div className={styles.contactLine}>
                            <div>
                                <Label>Ansprechpartner</Label>
                                <Select
                                    name="ansprechpartner"
                                    isDisabled={savingState === SavingState.saving}
                                    value={
                                        ansprechpartner
                                            ? {
                                                  value: ansprechpartner.nr.toString(),
                                                  label: getAnsprechpartnerLabel(ansprechpartner),
                                              }
                                            : {
                                                  value: '',
                                                  label: '',
                                              }
                                    }
                                    options={(kunde.data?.ansprechpartnerList || [])
                                        .sort((a, b) => a.name.localeCompare(b.name))
                                        .map((a) => ({
                                            value: a.nr.toString(),
                                            label: getAnsprechpartnerLabel(a),
                                        }))}
                                    onChange={(option: ValueType<SelectOption, false>): void => {
                                        setTermin({
                                            ...termin,
                                            kaNr: parseInt(option?.value || '', 10),
                                            kaName: option?.label || '',
                                        });
                                    }}
                                />
                                {ansprechpartner && (
                                    <div>
                                        {ansprechpartner.telefon && (
                                            <div className={formStyles.keyValueLine}>
                                                <div className={formStyles.dataKey100}>Telefon:</div>
                                                <div className={formStyles.dataValue}>
                                                    {ansprechpartner.telefon && (
                                                        <a href={'tel: ' + ansprechpartner.telefon}>
                                                            {ansprechpartner.telefon}
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {ansprechpartner.mobil && (
                                            <div className={formStyles.keyValueLine}>
                                                <div className={formStyles.dataKey100}>Mobil:</div>
                                                <div className={formStyles.dataValue}>
                                                    {ansprechpartner.mobil && (
                                                        <a href={'tel: ' + ansprechpartner.mobil}>
                                                            {ansprechpartner.mobil}
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {ansprechpartner.mail && (
                                            <div className={formStyles.keyValueLine}>
                                                <div className={formStyles.dataKey100}>Mail:</div>
                                                <div className={formStyles.dataValue}>
                                                    {ansprechpartner.mail && (
                                                        <a href={'mailto: ' + ansprechpartner.mail}>
                                                            {ansprechpartner.mail}
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {ansprechpartner.sprache && (
                                            <div className={formStyles.keyValueLine}>
                                                <div className={formStyles.dataKey100}>Sprache:</div>
                                                <div className={formStyles.dataValue}>
                                                    {SpracheLabel[ansprechpartner.sprache] || ansprechpartner.sprache}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div>
                                <Label>Mitarbeiter</Label>
                                <Select
                                    name="mitarbeiter"
                                    isDisabled={savingState === SavingState.saving}
                                    value={
                                        termin.userKz
                                            ? {
                                                  value: termin.userKz,
                                                  label: betreuerByKz[termin.userKz]?.name || termin.userKz,
                                              }
                                            : {
                                                  value: '',
                                                  label: '',
                                              }
                                    }
                                    options={betreuer
                                        .filter((b) => b.aktiv && b.name)
                                        .map((b) => ({
                                            value: b.kz,
                                            label: b.name,
                                        }))}
                                    onChange={(option: ValueType<SelectOption, false>): void => {
                                        setTermin({
                                            ...termin,
                                            userKz: option?.value || '',
                                        });
                                    }}
                                />
                            </div>
                            <div>
                                <Label>Art</Label>
                                <Select
                                    name="terminArt"
                                    isDisabled={savingState === SavingState.saving}
                                    value={
                                        termin.terminArt
                                            ? {
                                                  value: termin.terminArt,
                                                  label: TerminArtLabel[termin.terminArt] || termin.terminArt,
                                              }
                                            : {
                                                  value: '',
                                                  label: '',
                                              }
                                    }
                                    options={Object.keys(TerminArtLabel).map((key) => ({
                                        value: key,
                                        label: TerminArtLabel[key],
                                    }))}
                                    onChange={(option: ValueType<SelectOption, false>): void => {
                                        setTermin({
                                            ...termin,
                                            terminArt: option?.value || 'Z',
                                        });
                                    }}
                                />
                            </div>
                        </div>
                        <div className={formStyles.twoColumnsBlock}>
                            <div className={formStyles.firstColumn}>
                                <div className={styles.dateLine}>
                                    <DatePicker
                                        label="Datum von"
                                        disabled={savingState === SavingState.saving}
                                        className={formStyles.datePicker}
                                        firstDayOfWeek={DayOfWeek.Monday}
                                        strings={datePickerStrings}
                                        value={termin.datumVon ? moment(termin.datumVon).toDate() : new Date()}
                                        formatDate={onFormatDate}
                                        placeholder=""
                                        styles={{
                                            root: {
                                                marginBottom: 0,
                                            },
                                            callout: {
                                                selectors: {
                                                    '& .ms-DatePicker-goToday': { marginLeft: 196 },
                                                },
                                            },
                                        }}
                                        onSelectDate={(value) => {
                                            setTermin({
                                                ...termin,
                                                datumVon: moment(value).format('YYYY-MM-DD') + 'T00:00:00',
                                                datumBis: moment(value).format('YYYY-MM-DD') + 'T00:00:00',
                                            });
                                        }}
                                    />
                                    <TextField
                                        value={zeitVonS || ''}
                                        disabled={savingState === SavingState.saving}
                                        onChange={(event, newValue): void => {
                                            setZeitVonS(newValue || '');
                                            setZeitBisS(newValue || '');
                                            setTermin({
                                                ...termin,
                                                zeitVon: parseFloat((newValue || '').replace(',', '.')),
                                                zeitBis: parseFloat((newValue || '').replace(',', '.')),
                                            });
                                        }}
                                        label="Zeit von"
                                        placeholder="Zeit von"
                                    />
                                    <DatePicker
                                        label="Datum bis"
                                        disabled={savingState === SavingState.saving}
                                        className={formStyles.datePicker}
                                        firstDayOfWeek={DayOfWeek.Monday}
                                        strings={datePickerStrings}
                                        value={termin.datumBis ? moment(termin.datumBis).toDate() : new Date()}
                                        formatDate={onFormatDate}
                                        styles={{
                                            root: {
                                                marginBottom: 0,
                                            },
                                            callout: {
                                                selectors: {
                                                    '& .ms-DatePicker-goToday': { marginLeft: 196 },
                                                },
                                            },
                                        }}
                                        onSelectDate={(value) => {
                                            setTermin({
                                                ...termin,
                                                datumBis: moment(value).format('YYYY-MM-DD') + 'T00:00:00',
                                            });
                                        }}
                                    />
                                    <TextField
                                        value={zeitBisS || ''}
                                        disabled={savingState === SavingState.saving}
                                        onChange={(event, newValue): void => {
                                            setZeitBisS(newValue || '');
                                            setTermin({
                                                ...termin,
                                                zeitBis: parseFloat((newValue || '').replace(',', '.')),
                                            });
                                        }}
                                        label="Zeit von"
                                        placeholder="Zeit von"
                                    />
                                </div>
                                <div className={formStyles.line}>
                                    <TextField
                                        value={termin.terminOrt}
                                        disabled={savingState === SavingState.saving}
                                        onChange={(event, newValue): void => {
                                            setTermin({
                                                ...termin,
                                                terminOrt: newValue || '',
                                            });
                                        }}
                                        label="Ort"
                                        placeholder="Ort"
                                    />
                                </div>
                            </div>
                            <div className={formStyles.secondColumn}>
                                <div className={styles.detailsAndAttachments}>
                                    <div className={formStyles.firstColumn}>
                                        <div className={formStyles.line} style={{ marginTop: 15 }}>
                                            <Checkbox
                                                label="Strategische Info"
                                                disabled={savingState === SavingState.saving}
                                                className={formStyles.checkbox}
                                                checked={termin.prodInfo}
                                                onChange={(event, checked) => {
                                                    setTermin({
                                                        ...termin,
                                                        prodInfo: checked || false,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div className={formStyles.line}>
                                            <Label>Mailoptionen</Label>
                                            <Checkbox
                                                label="Mail an Kunden"
                                                disabled={savingState === SavingState.saving}
                                                className={formStyles.checkbox}
                                                checked={termin.mailKd || false}
                                                onChange={(event, checked) => {
                                                    setTermin({
                                                        ...termin,
                                                        mailKd: checked || false,
                                                    });
                                                }}
                                            />
                                            <Checkbox
                                                label="mit Beschreibung"
                                                className={formStyles.checkbox}
                                                disabled={savingState === SavingState.saving}
                                                checked={termin.mailKdBem || false}
                                                onChange={(event, checked) => {
                                                    setTermin({
                                                        ...termin,
                                                        mailKdBem: checked || false,
                                                    });
                                                }}
                                            />
                                            <Checkbox
                                                label="mit Kundendetails als PDF"
                                                disabled={savingState === SavingState.saving}
                                                className={formStyles.checkbox}
                                                checked={termin.mailPdf || false}
                                                onChange={(event, checked) => {
                                                    setTermin({
                                                        ...termin,
                                                        mailPdf: checked || false,
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className={formStyles.secondColumn}>
                                        {dbTermin.data?.terminAttachmentList && zfKaNr && zfKaNr !== -1 && (
                                            <TerminAttachmentList
                                                zfKaNr={zfKaNr}
                                                terminAttachmentList={dbTermin.data.terminAttachmentList}
                                                refetchTermin={() => dbTermin.refetch()}
                                                onAdd={() =>
                                                    setAddTerminAttachmentsData({
                                                        zfKaNr,
                                                        refetch: () => dbTermin.refetch(),
                                                    })
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={formStyles.oneColumnBlock}>
                            <div className={formStyles.line}>
                                <TextField
                                    value={termin.terminBetreff}
                                    disabled={savingState === SavingState.saving}
                                    onChange={(event, newValue): void => {
                                        setTermin({
                                            ...termin,
                                            terminBetreff: newValue || '',
                                        });
                                    }}
                                    label="Betreff"
                                    placeholder="Betreff"
                                />
                            </div>
                            <div className={formStyles.line}>{renderArtikelDetailSelect()}</div>
                            <div className={styles.artikelLine}>
                                <TextField
                                    value={termin.posText}
                                    disabled={savingState === SavingState.saving}
                                    label="Artikel-Text"
                                    placeholder="Artikel-Text..."
                                    onChange={(event, newValue) => {
                                        setTermin({
                                            ...termin,
                                            posText: newValue || '',
                                        });
                                    }}
                                />
                                <div>{renderEinheit('posEinh', 'Einheit')}</div>
                                <div>{renderDecimalField('posMenge', 'Menge', { fixed: 3 })}</div>
                            </div>
                            <div className={formStyles.line}>
                                <TextField
                                    value={termin.terminText}
                                    disabled={savingState === SavingState.saving}
                                    label="Beschreibung"
                                    placeholder="Beschreibung..."
                                    onChange={(event, newValue) => {
                                        setTermin({
                                            ...termin,
                                            terminText: newValue || '',
                                        });
                                    }}
                                    multiline
                                    resizable
                                    rows={10}
                                />
                            </div>
                        </div>

                        <Timestamps
                            createdDate={termin.anlageDat}
                            createdDecimalTime={termin.anlageUhr}
                            createdUser={termin.anlageUser}
                            modifiedDate={termin.updDat}
                            modifiedDecimalTime={termin.updUhr}
                            modifiedUser={termin.updUser}
                        />

                        {savingState === SavingState.error && (
                            <div className={formStyles.error}>Fehler beim Speichern.</div>
                        )}
                        <DialogFooter>
                            {savingState === SavingState.saving ? (
                                <PrimaryButton onClick={save} text="Speichere..." disabled />
                            ) : (
                                <PrimaryButton
                                    onClick={save}
                                    text="Speichern"
                                    disabled={
                                        savingState === SavingState.notChanged ||
                                        dbTermin.isLoading ||
                                        auth.rolle === 'EX'
                                    }
                                />
                            )}
                        </DialogFooter>
                    </form>
                )}
            </Dialog>
            {addTerminAttachmentData && (
                <AddTerminAttachments
                    zfKaNr={addTerminAttachmentData.zfKaNr}
                    onClose={(refetch2) => {
                        setAddTerminAttachmentsData(null);
                        if (refetch2) {
                            addTerminAttachmentData?.refetch();
                        }
                    }}
                />
            )}
        </>
    );
};
